<template>
  <div class="service-page">
    <!-- Carousel Hero Section -->
    <ServicesItsCarouselComp
      :slides="electricCarSlides"
      background-image="/resource/portfolio-03.jpg"
      pattern-image="/shapes/shape-06.png"
      :mission-vision="electricCarMissionVision"
    />

    <!-- Electric Vehicle Services Section -->
    <FullScreenSliderComp2
      :header-data="evServicesHeaderData"
      :services-data="evServicesData"
      :badge-data="evServicesBadgeData"
    />

    <!-- Green Innovation Section -->
    <section class="green-innovation-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-7">
            <div class="innovation-content">
              <h2 class="innovation-title">
                Drive the Future with Green Innovation
              </h2>
              <p class="innovation-description">
                Join the electric revolution and experience sustainable mobility
                with our advanced EV technology solutions. We're pioneering the
                future of transportation.
              </p>
              <div class="innovation-stats">
                <div class="stat-item">
                  <div class="stat-number">100%</div>
                  <div class="stat-label">Electric</div>
                </div>
                <div class="stat-item">
                  <div class="stat-number">Zero</div>
                  <div class="stat-label">Emissions</div>
                </div>
                <div class="stat-item">
                  <div class="stat-number">24/7</div>
                  <div class="stat-label">Charging</div>
                </div>
              </div>
              <button class="innovation-btn">Explore EV Solutions</button>
            </div>
          </div>
          <div class="col-lg-5">
            <img
              src="/images/resource/about-us.png"
              alt="Electric Vehicle Technology"
              class="w-100"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- EV Achievements Section -->
    <FullScreenSmoothSliderComp2
      :header-data="evAchievementsHeaderData"
      :achievements-data="evAchievementsData"
      :badge-data="evAchievementsBadgeData"
    />
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import WOW from "wowjs";
import ServicesItsCarouselComp from "@/components/ServicesItsCarouselComp.vue";
import FullScreenSliderComp2 from "@/components/FullScreenSliderComp2.vue";
import FullScreenSmoothSliderComp2 from "@/components/FullScreenSmoothSliderComp2.vue";

export default {
  name: "ElectricCarServicesPage",
  components: {
    ServicesItsCarouselComp,
    FullScreenSliderComp2,
    FullScreenSmoothSliderComp2,
  },
  setup() {
    const evServicesSwiper = ref(null);
    const evAchievementsSwiper = ref(null);

    // EV Services data for the slider
    const evServicesData = [
      {
        image: require("@/assets/resource/portfolio-01.jpg"),
        title: "Battery Health Monitoring",
        description:
          "Advanced diagnostic systems to monitor and optimize your EV battery performance",
        features: [
          "Battery Analysis",
          "Performance Optimization",
          "Health Reports",
        ],
      },
      {
        image: require("@/assets/resource/portfolio-02.jpg"),
        title: "Charging System Repair",
        description:
          "Expert repair and maintenance of all types of EV charging systems",
        features: ["Fast Charging", "Home Chargers", "Public Stations"],
      },
      {
        image: require("@/assets/resource/portfolio-03.jpg"),
        title: "Electric Motor Services",
        description:
          "Specialized maintenance and repair of electric vehicle motors",
        features: [
          "Motor Diagnostics",
          "Performance Tuning",
          "Efficiency Optimization",
        ],
      },
      {
        image: require("@/assets/resource/portfolio-04.jpg"),
        title: "Software Updates",
        description:
          "Latest firmware and software updates for optimal EV performance",
        features: ["OTA Updates", "Feature Enhancements", "Security Patches"],
      },
      {
        image: require("@/assets/resource/portfolio-01.jpg"),
        title: "Regenerative Braking",
        description:
          "Advanced regenerative braking system maintenance and optimization",
        features: ["Energy Recovery", "Brake Efficiency", "System Calibration"],
      },
      {
        image: require("@/assets/resource/portfolio-02.jpg"),
        title: "Thermal Management",
        description:
          "Comprehensive thermal management system services for optimal battery life",
        features: ["Cooling Systems", "Temperature Control", "Heat Management"],
      },
    ];

    // EV Achievements data for the slider
    const evAchievementsData = [
      {
        image: require("@/assets/resource/portfolio-01.jpg"),
        title: "Best EV Service Provider",
        category: "Electric Vehicle Excellence",
        date: "2024",
        tags: ["EV Innovation", "Service Excellence"],
      },
      {
        image: require("@/assets/resource/portfolio-02.jpg"),
        title: "Green Technology Leader",
        category: "Sustainable Transportation",
        date: "2024",
        tags: ["Green Tech", "Sustainability"],
      },
      {
        image: require("@/assets/resource/portfolio-03.jpg"),
        title: "Battery Innovation Award",
        category: "Advanced Battery Technology",
        date: "2023",
        tags: ["Battery Tech", "Innovation"],
      },
      {
        image: require("@/assets/resource/portfolio-04.jpg"),
        title: "EV Charging Excellence",
        category: "Charging Infrastructure",
        date: "2023",
        tags: ["Charging Solutions", "Infrastructure"],
      },
      {
        image: require("@/assets/resource/portfolio-01.jpg"),
        title: "Zero Emission Champion",
        category: "Environmental Impact",
        date: "2022",
        tags: ["Zero Emissions", "Environment"],
      },
      {
        image: require("@/assets/resource/portfolio-02.jpg"),
        title: "Future Mobility Pioneer",
        category: "Transportation Innovation",
        date: "2022",
        tags: ["Future Tech", "Mobility"],
      },
    ];

    const initEVServicesSwiper = () => {
      setTimeout(() => {
        const checkSwiper = () => {
          if (window.Swiper) {
            evServicesSwiper.value = new window.Swiper(".ev-services-slider", {
              loop: true,
              autoplay: {
                delay: 4000,
                disableOnInteraction: false,
              },
              navigation: {
                nextEl: ".next-btn",
                prevEl: ".prev-btn",
              },
              breakpoints: {
                0: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                },
                1200: {
                  slidesPerView: 4,
                  spaceBetween: 30,
                },
              },
            });
          } else {
            setTimeout(checkSwiper, 100);
          }
        };
        checkSwiper();
      }, 500);
    };

    const initEVAchievementsSwiper = () => {
      setTimeout(() => {
        const checkSwiper = () => {
          if (window.Swiper) {
            evAchievementsSwiper.value = new window.Swiper(
              ".ev-achievements-slider",
              {
                loop: true,
                autoplay: {
                  delay: 0,
                  disableOnInteraction: false,
                },
                speed: 5000,
                effect: "slide",
                allowTouchMove: false,
                breakpoints: {
                  0: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 30,
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                  },
                  1200: {
                    slidesPerView: 4,
                    spaceBetween: 30,
                  },
                },
              }
            );
          } else {
            setTimeout(checkSwiper, 100);
          }
        };
        checkSwiper();
      }, 500);
    };

    onMounted(async () => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false,
      }).init();

      // Import and initialize Owl Carousel
      try {
        await import("owl.carousel");

        // Initialize Owl Carousel for banner
        setTimeout(() => {
          if (window.$ && window.$.fn.owlCarousel) {
            window.$(".banner-carousel").owlCarousel({
              loop: true,
              margin: 0,
              nav: true,
              active: true,
              smartSpeed: 1000,
              autoplay: 6000,
              navText: [
                '<span class="icon-05"></span>',
                '<span class="icon-06"></span>',
              ],
              responsive: {
                0: {
                  items: 1,
                },
                600: {
                  items: 1,
                },
                768: {
                  items: 1,
                },
                992: {
                  items: 1,
                },
                1200: {
                  items: 1,
                },
              },
            });
          }
        }, 100);
      } catch (error) {
        console.error("Error loading Owl Carousel:", error);
      }

      // Initialize EV Services Swiper
      initEVServicesSwiper();
      initEVAchievementsSwiper();
    });

    // Electric Car Services slides data
    const electricCarSlides = ref([
      {
        subtitle: "Electric Car Services",
        titleLines: [
          { text: "Professional ", highlight: false },
          { text: "Electric Vehicle ", highlight: "Care" },
        ],
        description:
          "Specialized electric car services for all types of EVs. From battery maintenance to motor repairs, we provide expert care for your electric vehicle.",
        buttonText: "Get Service",
        buttonLink: "/contact",
      },
      {
        subtitle: "EV Specialists",
        titleLines: [{ text: "Certified ", highlight: "Technicians" }],
        description:
          "Our certified technicians specialize in electric vehicle systems, high-voltage battery technology, and advanced motor maintenance to keep your EV running efficiently.",
        buttonText: "Learn More",
        buttonLink: "/contact",
      },
      {
        subtitle: "Green Technology",
        titleLines: [{ text: "Sustainable ", highlight: "Solutions" }],
        description:
          "We offer comprehensive electric vehicle services including battery diagnostics, motor repair, charging system maintenance, and performance optimization for all EV models.",
        buttonText: "Book Now",
        buttonLink: "/contact",
      },
    ]);

    // Electric Car Services mission/vision data
    const electricCarMissionVision = ref({
      vision: {
        title: "Our Vision",
        description:
          "To be the leading electric vehicle service provider offering cutting-edge solutions for sustainable transportation with unmatched expertise and customer satisfaction.",
      },
      mission: {
        title: "Our Mission",
        description:
          "To deliver exceptional electric vehicle services using advanced diagnostic tools, specialized knowledge, and eco-friendly practices to ensure optimal EV performance and longevity.",
      },
    });

    // EV Services header data
    const evServicesHeaderData = {
      icon: "fas fa-bolt",
      tag: "ELECTRIC EXCELLENCE",
      title: "Electric Vehicle Services",
      description:
        "Revolutionary electric vehicle solutions powered by cutting-edge technology and sustainable innovation",
    };

    // EV Services badge data
    const evServicesBadgeData = {
      icon: "fas fa-bolt",
      text: "EV",
    };

    // EV Achievements header data
    const evAchievementsHeaderData = {
      icon: "fas fa-leaf",
      tag: "GREEN ACHIEVEMENTS",
      title: "EV Excellence Awards",
      description:
        "Recognized for our outstanding contributions to electric vehicle innovation and sustainable transportation",
    };

    // EV Achievements badge data
    const evAchievementsBadgeData = {
      icon: "fas fa-leaf",
      text: "Green Winner",
    };

    return {
      evServicesData,
      evServicesSwiper,
      evAchievementsData,
      evAchievementsSwiper,
      electricCarSlides,
      electricCarMissionVision,
      evServicesHeaderData,
      evServicesBadgeData,
      evAchievementsHeaderData,
      evAchievementsBadgeData,
    };
  },
};
</script>

<style scoped>
/* Import Google Fonts */
@import url("https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap");

/* Custom button styling for carousel - More specific to avoid conflicts */
.service-page .banner__two .btn-box .theme-btn {
  padding: 12px 30px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  border-radius: 0 !important;
  background: linear-gradient(219deg, #fb0e76 0%, #910694 100%) !important;
  color: #fff !important;
  text-decoration: none !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 5px 15px rgba(251, 14, 118, 0.4) !important;
  color: #fff !important;
  text-decoration: none !important;
}

.service-page .banner__two .btn-box .theme-btn i {
  font-size: 12px !important;
  transition: transform 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover i {
  transform: translateX(3px) !important;
}

/* Mission/Vision block styling */
.banner__mission__block {
  border-radius: 10px 0px 0px 0px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-top: 20px;
}

.mission__block {
  padding: 40px 50px;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  flex: 1;
}

.mission__block:last-child {
  border-right: none;
}

.mission__block h3 {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: "Archivo", sans-serif;
}

.mission__block p {
  color: #ccc;
  font-size: 14px;
  line-height: 24px;
  margin: 0;
  font-family: "Open Sans", sans-serif;
}

/* Reduce banner height - More specific to avoid conflicts */
.service-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

.service-page .banner__two .content-box {
  padding-bottom: 60px !important;
}

/* Responsive adjustments - More specific to avoid conflicts */
@media (max-width: 768px) {
  .service-page .banner__two .content-box h2 {
    font-size: 50px !important;
    line-height: 60px !important;
  }

  .service-page .mission__block {
    padding: 30px 25px !important;
  }

  .service-page .mission__block h3 {
    font-size: 18px !important;
  }

  .service-page .mission__block p {
    font-size: 13px !important;
    line-height: 22px !important;
  }
}

/* Electric Vehicle Services Section - Now handled by FullScreenSliderComp2 component */

/* Green Innovation Section */
.green-innovation-section {
  padding: 100px 0;
  background: #00061a;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.green-innovation-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(0, 255, 150, 0.1) 0%,
    rgba(0, 255, 200, 0.05) 25%,
    rgba(0, 255, 100, 0.1) 50%,
    rgba(0, 255, 150, 0.05) 75%,
    rgba(0, 255, 200, 0.1) 100%
  );
  pointer-events: none;
}

.innovation-content {
  padding: 40px 0;
  position: relative;
  z-index: 2;
}

.innovation-title {
  font-size: 48px;
  font-weight: 700;
  color: #00ff96;
  margin-bottom: 20px;
  line-height: 1.2;
  font-family: "Archivo", sans-serif;
}

.innovation-description {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 30px;
  max-width: 500px;
}

.innovation-stats {
  display: flex;
  gap: 30px;
  margin-bottom: 30px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 36px;
  font-weight: 700;
  color: #00ff96;
  margin-bottom: 5px;
  font-family: "Archivo", sans-serif;
}

.stat-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
}

.innovation-btn {
  background: linear-gradient(135deg, #00ff96 0%, #00ff64 100%);
  color: #000;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: none;
}

.innovation-btn:hover {
  background: linear-gradient(135deg, #00ff64 0%, #00ff96 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 255, 150, 0.3);
}

.innovation-visual {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  position: relative;
  z-index: 2;
}

.electric-animation {
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.battery-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 2s infinite;
}

.battery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  filter: brightness(1.2) saturate(1.3);
  transition: all 0.3s ease;
}

.battery-icon:hover .battery-image {
  transform: scale(1.1);
  filter: brightness(1.4) saturate(1.5);
}

.charging-bars {
  position: absolute;
  right: -50px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.bar {
  width: 20px;
  height: 30px;
  background: #00ff96;
  border-radius: 3px;
  animation: charge 2s infinite;
}

.bar-1 {
  animation-delay: 0s;
}
.bar-2 {
  animation-delay: 0.2s;
}
.bar-3 {
  animation-delay: 0.4s;
}
.bar-4 {
  animation-delay: 0.6s;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes charge {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}

/* EV Achievements Section - Now handled by FullScreenSmoothSliderComp2 component */

/* Responsive Design */
@media (max-width: 991px) {
  .section-header h2,
  .innovation-title {
    font-size: 36px;
  }

  .section-description,
  .innovation-description {
    font-size: 16px;
  }

  .innovation-stats {
    gap: 20px;
  }

  .stat-number {
    font-size: 28px;
  }
}

@media (max-width: 767px) {
  .section-header h2,
  .innovation-title {
    font-size: 28px;
  }

  .section-description,
  .innovation-description {
    font-size: 14px;
  }

  .innovation-stats {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }

  .stat-number {
    font-size: 24px;
  }

  /* EV sections now handled by components */
}

/* Import Flaticon */
@import "@/assets/scss/Flaticon.scss";

/* Global Styles */
body {
  font-family: "Open Sans", sans-serif;
  line-height: 1.6;
  color: #333;
}

.see__pad {
  padding: 100px 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

/* Pattern Layers */
.pattern-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.pattern-1 {
  top: 10%;
  right: 5%;
  width: 100px;
  height: 100px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-2 {
  bottom: 20%;
  left: 8%;
  width: 80px;
  height: 80px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-3 {
  top: 30%;
  left: 10%;
  width: 120px;
  height: 120px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-4 {
  top: 15%;
  right: 15%;
  width: 90px;
  height: 90px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-5 {
  bottom: 25%;
  right: 20%;
  width: 70px;
  height: 70px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-6 {
  top: 40%;
  left: 5%;
  width: 110px;
  height: 110px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.09;
}

.pattern-7 {
  bottom: 15%;
  left: 12%;
  width: 85px;
  height: 85px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-8 {
  top: 35%;
  right: 8%;
  width: 95px;
  height: 95px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

/* Intro Section */
.service-intro {
  background: #00061a;
  position: relative;
  overflow: hidden;
}

.service-intro-content {
  position: relative;
  z-index: 2;
}

.service-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #f6ec4f;
  margin-bottom: 20px;
  font-family: "Archivo", sans-serif;
  text-transform: uppercase;
}

.service-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: "Archivo", sans-serif;
}

.service-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

/* Services Offered Section */
.services-offered {
  background: #00061a;
  position: relative;
  overflow: hidden;
}

.services-header {
  position: relative;
  z-index: 2;
  margin-bottom: 60px;
}

.services-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #f6ec4f;
  margin-bottom: 20px;
  font-family: "Archivo", sans-serif;
  text-transform: uppercase;
}

.services-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: "Archivo", sans-serif;
}

.services-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

.service-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(246, 236, 79, 0.1);
  border-radius: 15px;
  padding: 40px 30px;
  height: 100%;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
}

.service-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(246, 236, 79, 0.2);
  border-color: #f6ec4f;
}

.service-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.block-shape-1 {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: linear-gradient(
    135deg,
    rgba(246, 236, 79, 0.1),
    rgba(246, 236, 79, 0.05)
  );
  border-radius: 50%;
  opacity: 0.3;
}

.block-shape-2 {
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 80px;
  height: 80px;
  background: linear-gradient(
    45deg,
    rgba(246, 236, 79, 0.08),
    rgba(246, 236, 79, 0.03)
  );
  border-radius: 50%;
  opacity: 0.4;
}

.service-content {
  position: relative;
  z-index: 2;
}

.service-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #f6ec4f, #e6d43a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
}

.service-icon i {
  font-size: 24px;
  color: #00061a;
}

.service-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  font-family: "Archivo", sans-serif;
}

.service-content p {
  font-size: 16px;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.6;
}

.service-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-content ul li {
  color: #fff;
  font-size: 14px;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.service-content ul li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #f6ec4f;
  font-weight: bold;
}

/* Call to Action Section */
.service-cta {
  background: #00061a;
  position: relative;
  overflow: hidden;
}

.cta-content {
  position: relative;
  z-index: 2;
}

.cta-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: "Archivo", sans-serif;
}

.cta-description p {
  font-size: 18px;
  color: #fff;
  max-width: 600px;
  margin: 0 auto 40px;
  line-height: 1.8;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.primary-btn {
  background: linear-gradient(135deg, #f6ec4f, #e6d43a);
  color: #00061a;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(246, 236, 79, 0.3);
}

.secondary-btn {
  background: transparent;
  color: #f6ec4f;
  border-color: #f6ec4f;
}

.secondary-btn:hover {
  background: #f6ec4f;
  color: #00061a;
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 767px) {
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 36px;
    line-height: 46px;
  }

  .service-section-label h4,
  .services-section-label h4 {
    letter-spacing: 8px;
    font-size: 16px;
  }

  .service-item {
    padding: 30px 20px;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }

  .cta-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}

@media (max-width: 399px) {
  .see__pad {
    padding: 60px 0;
  }

  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 28px;
    line-height: 38px;
  }
}
</style> 